/**
 * @module store/modules/supervisor/mutations
 * @description Supervisor Vuex mutations for managing state.
 */

import Vue from "vue";
import { MutationTree } from "vuex";
import { SupervisorState } from "./types";
import { LivechatSessionType } from "@/store/modules/livechat/types";

const mutations: MutationTree<SupervisorState> = {
  /**
   * Sets the current supervisor view mode.
   *
   * @param {SupervisorState} state - Current module state
   * @param {string} payload - Mode name (e.g., 'ONLINE_AGENTS', 'OFFLINE_AGENTS')
   */
  SET_CURRENT_MODE(state, payload) {
    Vue.set(state, "currentMode", payload);
  },
  /**
   * Sets the selected chat session for viewing.
   *
   * @param {SupervisorState} state - Current module state
   * @param {LivechatSessionType} payload - Chat session data
   *
   * @todo Reduce payload structure for better performance
   */
  SET_SELECTED_CHAT_SESSION(state, payload: LivechatSessionType) {
    //TODO: reduce payload structure
    Vue.set(state, "selectedChatSession", payload);
  },
  /**
   * Sets the currently expanded agent or chat panel.
   *
   * @param {SupervisorState} state - Current module state
   * @param {string|null} payload - Expanded panel identifier
   */
  SET_CURRENT_EXPANDED(state, payload) {
    if (state.currentMode && payload) {
      Vue.set(state, "currentMode", null);
    }
    Vue.set(state, "currentExpanded", payload);
  },
  /**
   * Sets the selected department filter.
   *
   * @param {SupervisorState} state - Current module state
   * @param {string[]} payload - Array of department IDs to filter by
   */
  SET_DEPARTMENT(state, payload) {
    Vue.set(state, "selectedDepartmentFilter", payload);
  },
  /**
   * Sets the selected agent email.
   *
   * @param {SupervisorState} state - Current module state
   * @param {Object} payload - Payload object
   * @param {string} payload.email - Agent email address
   */
  SET_SELECTED_AGENT(state, { email }: { email: String }) {
    Vue.set(state, "selectedAgent", email);
  },
  /**
   * Sets the list of agents with their chat counts.
   *
   * @param {SupervisorState} state - Current module state
   * @param {Array} payload - Array of agent objects with chat counts
   */
  SET_AGENTS_WITH_CHAT_COUNT(state, payload) {
    Vue.set(state, "agentsWithChatCount", payload);
  },

  /**
   * Sets a loading state flag by type.
   *
   * @param {SupervisorState} state - Current module state
   * @param {Object} payload - Payload object
   * @param {string} payload.type - Loading state type
   * @param {boolean} payload.value - Loading state value
   */
  SET_LOADING_STATE(state, { type, value }: { type: string; value: boolean }) {
    Vue.set(state, `${type}`, value);
  },

  /**
   * Sets the in-queue chats array and count.
   *
   * @param {SupervisorState} state - Current module state
   * @param {Object} payload - Payload object
   * @param {Array} payload.data - Array of chat sessions
   * @param {number} payload.count - Total count
   */
  SET_IN_QUEUE_CHATS(state, { data, count }) {
    Vue.set(state, "inQueueChatsArray", data);
    Vue.set(state, "inQueueChatCount", count);
  },
  /**
   * Sets the on-going chats array and count.
   *
   * @param {SupervisorState} state - Current module state
   * @param {Object} payload - Payload object
   * @param {Array} payload.data - Array of chat sessions
   * @param {number} payload.count - Total count
   */
  SET_ON_GOING_CHATS(state, { data, count }) {
    Vue.set(state, "onGoingChatsArray", data);
    Vue.set(state, "onGoingChatCount", count);
  },
  /**
   * Sets the abandoned chats array and count.
   *
   * @param {SupervisorState} state - Current module state
   * @param {Object} payload - Payload object
   * @param {Array} payload.data - Array of chat sessions
   * @param {number} payload.count - Total count
   */
  SET_ABANDONED_CHATS(state, { data, count }) {
    Vue.set(state, "abandonedChatsArray", data);
    Vue.set(state, "abandonedChatCount", count);
  },
  /**
   * Sets the resolved chats array and count.
   *
   * @param {SupervisorState} state - Current module state
   * @param {Object} payload - Payload object
   * @param {Array} payload.data - Array of chat sessions
   * @param {number} payload.count - Total count
   */
  SET_RESOLVED_CHATS(state, { data, count }) {
    Vue.set(state, "resolvedChatsArray", data);
    Vue.set(state, "resolvedChatCount", count);
  },

  INCREMENT_AGENT_CHAT_COUNT(state, agentEmail: string) {
    const email = agentEmail?.toLowerCase?.();
    if (!email) return;

    const idx = (state.agentsWithChatCount as any[])?.findIndex(
      (e) => e.agent?.email?.toLowerCase?.() === email
    );

    if (idx < 0) return;

    const entry = (state.agentsWithChatCount[idx] ?? {}) as any;

    const updated = { ...entry, chatCount: (entry.chatCount ?? 0) + 1 };

    const arr: any[] = [...state.agentsWithChatCount];

    arr[idx] = updated;

    Vue.set(state, "agentsWithChatCount", arr);
  },

  DECREMENT_AGENT_CHAT_COUNT(state, agentEmail: string) {
    const email = agentEmail?.toLowerCase?.();
    if (!email) return;
    const idx = (state.agentsWithChatCount as any[])?.findIndex(
      (e) => e.agent?.email?.toLowerCase?.() === email
    );
    if (idx < 0 || (state.agentsWithChatCount[idx] as any).chatCount <= 0) return;
    const entry = (state.agentsWithChatCount[idx] ?? {}) as any;

    const updated = { ...entry, chatCount: entry.chatCount - 1 };

    const arr: any[] = [...state.agentsWithChatCount];

    arr[idx] = updated;

    Vue.set(state, "agentsWithChatCount", arr);
  },

  ADD_AGENT_CHAT(state, { agentEmail, chatId }: { agentEmail: string; chatId: string }) {
    const email = agentEmail?.toLowerCase?.();
    if (!email || !chatId) return;

    const currentChats = state.agentChatIds[email] || [];

    if (!currentChats.includes(chatId)) {
      Vue.set(state.agentChatIds, email, [...currentChats, chatId]);
    }
  },

  REMOVE_AGENT_CHAT(state, { agentEmail, chatId }: { agentEmail: string; chatId: string }) {
    const email = agentEmail?.toLowerCase?.();

    if (!email || !chatId) return;

    const currentChats = state.agentChatIds[email] || [];
    const filteredChats = currentChats.filter((id) => id !== chatId);

    Vue.set(state.agentChatIds, email, filteredChats);
  },

  INIT_AGENT_CHATS(
    state,
    { chats }: { chats: Array<{ agents?: string[]; RowKey?: string; id?: string }> }
  ) {
    const agentChatIds: { [email: string]: string[] } = {};

    chats.forEach((chat) => {
      const chatId = chat.RowKey || chat.id;
      if (!chatId || !chat.agents) return;

      chat.agents.forEach((agentEmail) => {
        const email = agentEmail?.toLowerCase?.();
        if (!email) return;

        if (!agentChatIds[email]) {
          agentChatIds[email] = [];
        }

        if (!agentChatIds[email].includes(chatId)) {
          agentChatIds[email].push(chatId);
        }
      });
    });

    Vue.set(state, "agentChatIds", agentChatIds);
  },

  CLEAR_AGENT_CHATS(state) {
    Vue.set(state, "agentChatIds", {});
  },

  SET_LOCK_ON_LIVE_ANALYTICS(state, payload) {
    Vue.set(state, "isLockOnLiveAnalytics", payload);
  },
};

export default mutations;
